import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { personSystemAlias } from '../../utils/personnel';
import AssignmentBlock from './AssignmentBlock';
import { getSectionNote, getSectionTime, getSectionTitle, getSectionViewState } from './staffSectionView';
import './StaffEditor.css';

const EMPTY_ASSIGNMENT_MODEL = {
  defaultSection: '',
  singleEntityPhase: '',
  labels: { person: '', section: '', empty: '' },
  phases: [],
  quickRoles: [],
  personnelCategories: [],
};

// Fijos en el backend (ver backend/src/services/staffAssignmentModel.js).
// Se usan sólo para no perder la estructura de columnas del grid (armado /
// operación / desarmado) cuando el modelo real no pudo cargarse — nunca para
// editar asignaciones, esa lógica sigue exigiendo el modelo real.
const FALLBACK_PHASE_KEYS = ['armado', 'evento', 'desarmado'];
const FALLBACK_PHASE_LABELS = { armado: 'Armado', evento: 'Operación', desarmado: 'Desarmado' };
const FALLBACK_SINGLE_ENTITY_PHASE = 'evento';

// Drag/drop state compartido entre todas las instancias para cross-event DnD
let staffDrag = null;
let staffDropTarget = null; // { addFn, phase, section, entityKey, clearFn }
const staffDropRegistry = new Map();
let sectionDrag = null;
let sectionDropTarget = null; // { moveFn, phase, eventId, clearFn }
let tmpSeq = 0; // ids temporales únicos (Date.now() colisiona al marcar rápido)
const stableAssignmentCache = new Map();
const STABLE_ASSIGNMENT_CACHE_MS = 8000;

function sortAssignments(list) {
  return [...(list || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

function readStableAssignments(entityKey, fallback) {
  const cached = stableAssignmentCache.get(entityKey);
  if (cached && Date.now() - cached.at < STABLE_ASSIGNMENT_CACHE_MS) {
    const fallbackList = fallback || [];
    if (!fallbackList.length && cached.items.length) return sortAssignments(cached.items);
  }
  return sortAssignments(fallback);
}

function writeStableAssignments(entityKey, items) {
  stableAssignmentCache.set(entityKey, {
    at: Date.now(),
    items: sortAssignments(items).map(item => ({ ...item })),
  });
}

function norm(s) {
  return String(s || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function pillKey(phase, name) {
  return `${phase}::${norm(name)}`;
}

function isLegacyOperationalAssignment(item) {
  const name = norm(item?.person_name);
  if (!name) return false;
  return [
    'ya esta armado',
    'ya armado',
    'queda armado',
    'desarmado al otro dia',
    'se arma tal dia',
    'se desarma tal dia',
  ].includes(name);
}

function clampTime(hh, mm) {
  const h = Math.max(0, Math.min(23, Number(hh)));
  const m = Math.max(0, Math.min(59, Number(mm)));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function normalizeTimeText(value) {
  const raw = String(value || '').trim().replace('.', ':');
  if (!raw) return '';
  if (raw.includes(':')) {
    const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);
    if (!match) return raw;
    return clampTime(match[1], match[2]);
  }
  if (/^\d{1,2}$/.test(raw)) return clampTime(raw, 0);
  if (/^\d{3}$/.test(raw)) return clampTime(raw.slice(0, 1), raw.slice(1));
  if (/^\d{4}$/.test(raw)) return clampTime(raw.slice(0, 2), raw.slice(2));
  return raw;
}

function personnelAlias(p) {
  return personSystemAlias(p);
}

function groupSectionsByPhase(initialSections) {
  const grouped = {};
  if (!Array.isArray(initialSections)) return grouped;
  for (const section of initialSections) {
    if (!section?.phase) continue;
    if (!grouped[section.phase]) grouped[section.phase] = [];
    grouped[section.phase].push(section);
  }
  Object.keys(grouped).forEach(phase => {
    grouped[phase].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  });
  return grouped;
}

export default function StaffEditor({ eventId, taskId = null, armadoId = null, operationId = null, initialAssignments = [], initialSections = null, assignmentModel: assignmentModelProp = null, assignmentModelError = false, retryAssignmentModel = null, personnel = [], canEdit, tabGroup, tabIndexStart, entityDate = null, startTime = null, endTime = null, externalConflictKeys = undefined, onCreateAuxOperation = null, touchPicker = false }) {
  const [assignmentModel, setAssignmentModel] = useState(() => ({
    ...EMPTY_ASSIGNMENT_MODEL,
    ...(assignmentModelProp || {}),
    labels: { ...EMPTY_ASSIGNMENT_MODEL.labels, ...((assignmentModelProp && assignmentModelProp.labels) || {}) },
  }));
  // El fetch centralizado (useAgendaData) falló y no hay modelo previo en
  // props: sin esto la celda queda en blanco sin distinguirse de "nadie
  // asignado". No se usa el estado local `assignmentModel` acá porque ese
  // puede quedar con un valor viejo por el fallback de auto-fetch de abajo.
  const modelUnavailable = Boolean(assignmentModelError) && !assignmentModelProp;
  const [modelRetrying, setModelRetrying] = useState(false);
  async function handleRetryAssignmentModel() {
    if (!retryAssignmentModel || modelRetrying) return;
    setModelRetrying(true);
    try {
      await retryAssignmentModel();
    } finally {
      setModelRetrying(false);
    }
  }
  const singlePhase = !!(taskId || armadoId || operationId);
  const allPhases = assignmentModel.phases.length
    ? assignmentModel.phases.map(phase => phase.key)
    : (modelUnavailable ? FALLBACK_PHASE_KEYS : []);
  const phases = singlePhase
    ? [assignmentModel.singleEntityPhase || (modelUnavailable ? FALLBACK_SINGLE_ENTITY_PHASE : '')].filter(Boolean)
    : allPhases;
  const phaseKey = phases.join('|');
  const phaseLabels = Object.fromEntries(assignmentModel.phases.map(phase => [phase.key, phase.label]));
  const defaultSection = assignmentModel.defaultSection;
  const categoryOrder = assignmentModel.personnelCategories || [];
  const entityParam = taskId ? { task_id: taskId }
                    : armadoId ? { armado_id: armadoId }
                    : operationId ? { operation_id: operationId }
                    : { event_id: eventId };
  const entityKey = taskId ? `task:${taskId}`
                  : armadoId ? `armado:${armadoId}`
                  : operationId ? `operation:${operationId}`
                  : `event:${eventId}`;
  const currentEntityLabel = taskId ? 'Asignado a la tarea'
                           : armadoId ? 'Asignado al armado'
                           : operationId ? 'Asignado al desarmado'
                           : 'Asignado al evento';
  const excludeParam = taskId ? { exclude_task_id: taskId }
                     : armadoId ? { exclude_armado_id: armadoId }
                     : operationId ? { exclude_operation_id: operationId }
                     : { exclude_event_id: eventId };
  const [assignments, setAssignments] = useState(() =>
    readStableAssignments(entityKey, initialAssignments)
  );
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [clipboard, setClipboard] = useState([]);
  const [activePhase, setActivePhase] = useState(null);
  const [pendingRole, setPendingRole] = useState(null);
  const [searchPhase, setSearchPhase] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPos, setMenuPos] = useState(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [dropPhase, setDropPhase] = useState(null);
  const [dropSectionKey, setDropSectionKey] = useState(null);
  const [dragReorder, setDragReorder] = useState(null);
  const [sectionDropMarker, setSectionDropMarker] = useState(null);
  const dragReorderRef = useRef(null);
  const [sections, setSections] = useState(() => groupSectionsByPhase(initialSections));
  const [searchSection, setSearchSection] = useState(defaultSection);
  const [renamingSection, setRenamingSection] = useState(null);
  const [draftSectionIds, setDraftSectionIds] = useState(() => new Set());
  const [editingSectionMeta, setEditingSectionMeta] = useState(null);
  const [sectionMenu, setSectionMenu] = useState(null);
  const [editingSectionNote, setEditingSectionNote] = useState(null);
  const [preservedEmptySections, setPreservedEmptySections] = useState(() => new Set());
  const [conflictKeys, setConflictKeys] = useState(
    externalConflictKeys instanceof Set ? externalConflictKeys : new Set()
  );
  const [bookedPersonIds, setBookedPersonIds] = useState(new Set());
  const [bookedPersonLabels, setBookedPersonLabels] = useState(new Map());
  const [bookedPersonRoles, setBookedPersonRoles] = useState(new Map());
  const [bookedPersonNameLabels, setBookedPersonNameLabels] = useState(new Map());
  const [bookedPersonNameRoles, setBookedPersonNameRoles] = useState(new Map());
  const [bookedPersonMetaById, setBookedPersonMetaById] = useState(new Map());
  const [bookedPersonMetaByName, setBookedPersonMetaByName] = useState(new Map());
  const [touchSheet, setTouchSheet] = useState(null);

  const searchRefs = useRef({});
  const sectionTitleRefs = useRef({});
  const sectionTimeRefs = useRef({});
  const lineRefs = useRef({});
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const sectionDragRef = useRef(null);
  const ensureSectionRef = useRef({});
  const dropPhaseRef = useRef(null);
  const dropSectionKeyRef = useRef(null);
  const sectionDropMarkerRef = useRef(null);
  const suppressSectionClickRef = useRef(false);
  const assignmentsRef = useRef(assignments);
  const sectionsRef = useRef(sections);
  const pendingOpsRef = useRef(0);
  const ignoreRegressiveInitialUntilRef = useRef(0);
  const cancelledTmpRef = useRef(new Set());

  useEffect(() => {
    assignmentsRef.current = assignments;
    writeStableAssignments(entityKey, assignments);
  }, [assignments, entityKey]);
  useEffect(() => { sectionsRef.current = sections; }, [sections]);

  useEffect(() => {
    if (assignmentModelProp) {
      setAssignmentModel({
        ...EMPTY_ASSIGNMENT_MODEL,
        ...assignmentModelProp,
        labels: { ...EMPTY_ASSIGNMENT_MODEL.labels, ...(assignmentModelProp.labels || {}) },
        phases: Array.isArray(assignmentModelProp.phases) ? assignmentModelProp.phases : [],
        quickRoles: Array.isArray(assignmentModelProp.quickRoles) ? assignmentModelProp.quickRoles : [],
        personnelCategories: Array.isArray(assignmentModelProp.personnelCategories) ? assignmentModelProp.personnelCategories : [],
      });
      return undefined;
    }
    let mounted = true;
    api.get('/staff-assignments/model')
      .then(({ data }) => {
        if (!mounted || !data) return;
        setAssignmentModel({
          ...EMPTY_ASSIGNMENT_MODEL,
          ...data,
          labels: { ...EMPTY_ASSIGNMENT_MODEL.labels, ...(data.labels || {}) },
          phases: Array.isArray(data.phases) ? data.phases : [],
          quickRoles: Array.isArray(data.quickRoles) ? data.quickRoles : [],
          personnelCategories: Array.isArray(data.personnelCategories) ? data.personnelCategories : [],
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [assignmentModelProp]);

  useEffect(() => {
    if (!tabGroup || tabIndexStart === undefined) return;
    phases.forEach((phase, i) => {
      tabGroup.current[tabIndexStart + i] = () => {
        setSearchPhase(phase);
        setSearchSection(defaultSection);
        setActivePhase(phase);
        setTimeout(() => searchRefs.current[`${phase}::${defaultSection}`]?.focus(), 0);
      };
    });
  });

  function advancePhaseTab(currentPhase, reverse) {
    if (!tabGroup || tabIndexStart === undefined) return false;
    const idx = phases.indexOf(currentPhase);
    if (idx === -1) return false;
    const fn = tabGroup.current[tabIndexStart + idx + (reverse ? -1 : 1)];
    if (typeof fn !== 'function') return false;
    fn();
    return true;
  }

  // Resync desde el padre SOLO cuando el contenido cambió de verdad. El padre
  // crea un array nuevo en cada render (`event.staff_assignments || []`), así
  // que comparar por identidad reaplicaría los datos del padre en cada render
  // y pisaría lo recién marcado → la gente "se sale del casillero"/"se borra".
  // Nunca mientras hay ops optimistas en vuelo.
  const lastInitSig = useRef(null);
  useEffect(() => {
    if (pendingOpsRef.current > 0) return;
    const sorted = readStableAssignments(entityKey, initialAssignments);
    if (
      Date.now() < ignoreRegressiveInitialUntilRef.current
      && sorted.length < assignmentsRef.current.length
    ) {
      return;
    }
    const sig = JSON.stringify(
      sorted.map(a => [a.id, a.phase, a.person_name, a.role, a.section || defaultSection, a.sort_order])
    );
    if (sig === lastInitSig.current) return;
    lastInitSig.current = sig;
    setAssignments(sorted);
  }, [initialAssignments, entityKey]);

  const lastSectionsSig = useRef(null);
  useEffect(() => {
    if (!Array.isArray(initialSections)) return;
    if (pendingOpsRef.current > 0) return;
    const sig = JSON.stringify(
      initialSections.map(s => [s.id, s.phase, s.name, s.title, s.note, s.time_text, s.kind, s.sort_order])
    );
    if (sig === lastSectionsSig.current) return;
    lastSectionsSig.current = sig;
    setSections(groupSectionsByPhase(initialSections));
  }, [initialSections]);

  useEffect(() => {
    if (Array.isArray(initialSections)) return;
    fetchSections();
  }, []);

  async function fetchSections() {
    try {
      const { data } = await api.get('/staff-assignments/sections', { params: entityParam });
      const grouped = {};
      for (const s of data) {
        if (!grouped[s.phase]) grouped[s.phase] = [];
        grouped[s.phase].push(s);
      }
      setSections(grouped);
    } catch { /* silencioso */ }
  }

  function getSectionsForPhase(phase) {
    const explicit = sections[phase] || [];
    const assignmentSectionNames = new Set(
      assignmentsRef.current
        .filter(a => a.phase === phase && !isLegacyOperationalAssignment(a))
        .map(a => a.section || defaultSection)
    );
    const merged = [...explicit];
    for (const name of assignmentSectionNames) {
      if (!merged.find(s => s.name === name)) merged.push({ id: null, name, sort_order: 999 });
    }
    merged.sort((a, b) => {
      if (a.name === defaultSection) return -1;
      if (b.name === defaultSection) return 1;
      return a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'es');
    });
    if (!merged.length) merged.push({ id: null, name: defaultSection, sort_order: 0 });
    return merged;
  }

  async function renameSection(sectionId, phase, oldName, newName) {
    const trimmed = newName.trim();
    setRenamingSection(null);
    setEditingSectionMeta(null);
    const current = sectionId?.id ? sectionId : (sections[phase] || []).find(s => s.id === sectionId);
    if (trimmed === sectionTitle(current)) {
      return;
    }
    if (!trimmed && !current?.id) return;
    try {
      const target = current?.id ? current : await ensureSectionRecord(current || { name: oldName || defaultSection }, phase);
      if (!target?.id) return;
      const { data } = await api.patch(`/staff-assignments/sections/${target.id}`, { title: trimmed });
      setSections(prev => ({
        ...prev,
        [phase]: mergeSection(prev[phase] || [], data),
      }));
    } catch { /* silencioso */ }
  }

  function mergeSection(list, section) {
    const others = list.filter(s => s.id !== section.id && s.name !== section.name);
    return [...others, section].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }

  async function ensureSectionRecord(section, phase) {
    if (section?.id) return section;
    if (!canEdit) return null;
    const name = String(section?.name || defaultSection).trim() || defaultSection;
    const existing = (sections[phase] || []).find(s => s.id && s.name === name);
    if (existing) return existing;
    const ensureKey = `${phase}::${name}`;
    if (ensureSectionRef.current[ensureKey]) {
      return ensureSectionRef.current[ensureKey];
    }
    const explicit = sections[phase] || [];
    const sortOrder = name === defaultSection
      ? 0
      : explicit.length
        ? Math.max(...explicit.map(s => Number(s.sort_order) || 0)) + 1
        : 1;
    const createPromise = (async () => {
      const { data } = await api.post('/staff-assignments/sections', {
        ...entityParam,
        phase,
        name,
        title: '',
        note: '',
        time_text: '',
        kind: 'assignment',
        sort_order: sortOrder,
      });
      setSections(prev => ({
        ...prev,
        [phase]: mergeSection(prev[phase] || [], data),
      }));
      return assignmentsRef.current.find(a => a.id === data.id) || data;
    })()
      .catch(() => null)
      .finally(() => { delete ensureSectionRef.current[ensureKey]; });
    ensureSectionRef.current[ensureKey] = createPromise;
    return createPromise;
  }

  async function ensureSectionPersistsWhenEmptied(phase, sectionName, itemsAfterRemoval) {
    const name = sectionName || defaultSection;
    if (!name || name === defaultSection) return null;
    const stillHasItems = itemsAfterRemoval.some(a => a.phase === phase && (a.section || defaultSection) === name);
    if (stillHasItems) return null;
    const current = (sectionsRef.current[phase] || []).find(s => s.name === name);
    if (current?.id) return current;
    return ensureSectionRecord(current || { name }, phase);
  }

  async function patchSection(sectionId, phase, patch) {
    if (!sectionId) return null;
    try {
      const { data } = await api.patch(`/staff-assignments/sections/${sectionId}`, patch);
      setDraftSectionIds(prev => {
        if (!prev.has(sectionId)) return prev;
        const next = new Set(prev);
        if (
          String(data?.title || '').trim()
          || String(data?.note || '').trim()
          || String(data?.time_text || '').trim()
          || data?.kind === 'status'
        ) next.delete(sectionId);
        return next;
      });
      setSections(prev => ({
        ...prev,
        [phase]: mergeSection(prev[phase] || [], data),
      }));
      return data;
    } catch {
      return null;
    }
  }

  async function patchSectionMeta(section, phase, patch) {
    const hasMeaningfulValue = Object.entries(patch).some(([key, value]) => {
      if (key === 'kind') return false;
      return String(value ?? '').trim();
    });
    if (!section?.id && !hasMeaningfulValue) return null;
    const target = section?.id ? section : await ensureSectionRecord(section, phase);
    if (!target?.id) return null;
    return patchSection(target.id, phase, patch);
  }

  function beginSectionMetaEdit(phase, section) {
    setActivePhase(phase);
    setSearchSection(section.name);
    setEditingSectionMeta({ phase, sectionName: section.name });
    setSearchPhase(null);
    setSearchQuery('');
    setMenuPos(null);
    setPendingRole(null);
    setHighlightIndex(0);
  }

  function sectionRefKey(phase, sectionName) {
    return `${phase}::${sectionName}`;
  }

  function preserveEmptySection(phase, sectionName) {
    const key = sectionRefKey(phase, sectionName || defaultSection);
    setPreservedEmptySections(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  function focusSectionMeta(phase, section, field = 'title') {
    beginSectionMetaEdit(phase, section);
    const refMap = field === 'time' ? sectionTimeRefs : sectionTitleRefs;
    const key = sectionRefKey(phase, section.name);
    setTimeout(() => {
      const input = refMap.current[key];
      input?.focus();
      input?.select?.();
    }, 0);
  }

  async function beginSectionNoteEdit(section, phase) {
    const target = section?.id ? section : await ensureSectionRecord(section, phase);
    if (!target?.id) return;
    setActivePhase(phase);
    setSearchSection(target.name);
    setEditingSectionMeta(null);
    setSearchPhase(null);
    setSearchQuery('');
    setMenuPos(null);
    setPendingRole(null);
    setEditingSectionNote(target.id);
  }

  function handleStatusSectionAction(action, phase, section, anchorEl = null) {
    setSectionMenu(null);
    if (action === 'delete') {
      deleteSection(section?.id, phase, section?.name);
      return;
    }
    if (action === 'change-date' || action === 'change-days') {
      onCreateAuxOperation?.(phase, anchorEl, {
        replaceExisting: true,
        replaceSectionId: section?.id || null,
      });
    }
  }

  function stopSectionChromeEvent(e) {
    e.stopPropagation();
  }

  async function createSection(phase) {
    if (!canEdit) return null;
    const explicit = sections[phase] || [];
    const sortOrder = explicit.length
      ? Math.max(...explicit.map(s => Number(s.sort_order) || 0)) + 1
      : 1;
    const baseName = 'Bloque';
    let n = explicit.length + 1;
    let name = `${baseName} ${n}`;
    const used = new Set(explicit.map(s => norm(s.name)));
    while (used.has(norm(name))) {
      name = `${baseName} ${++n}`;
    }
    try {
      const { data } = await api.post('/staff-assignments/sections', {
        ...entityParam,
        phase,
        name,
        title: '',
        kind: 'assignment',
        sort_order: sortOrder,
      });
      setSections(prev => ({
        ...prev,
        [phase]: mergeSection(prev[phase] || [], data),
      }));
      setActivePhase(phase);
      setSearchSection(data.name);
      setSearchQuery('');
      setMenuPos(null);
      setPendingRole(null);
      setDraftSectionIds(prev => new Set(prev).add(data.id));
      setSearchPhase(phase);
      setRenamingSection(null);
      setEditingSectionMeta(null);
      fetchBookedPersons();
      setTimeout(() => {
        searchRefs.current[`${phase}::${data.name}`]?.focus();
      }, 30);
      return data;
    } catch {
      return null;
    }
  }

  function removeLocalSection(phase, sectionName) {
    setAssignments(prev => prev.filter(a => !(a.phase === phase && (a.section || defaultSection) === sectionName)));
    setSections(prev => ({
      ...prev,
      [phase]: (prev[phase] || []).filter(s => s.name !== sectionName),
    }));
  }

  function insertMovedSection(phase, section, movedAssignments) {
    setSections(prev => {
      const others = (prev[phase] || []).filter(s => s.id !== section.id && s.name !== section.name);
      return {
        ...prev,
        [phase]: [...others, section].sort((a, b) => a.sort_order - b.sort_order),
      };
    });
    setAssignments(prev => [
      ...prev.filter(a => !(a.phase === phase && (a.section || defaultSection) === section.name)),
      ...movedAssignments,
    ]);
  }

  async function reorderSectionInPhase(phase, section, toIndex) {
    if (!section?.id || section.name === defaultSection) return;
    const current = (sectionsRef.current[phase] || [])
      .filter(s => s.id && s.name !== defaultSection)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const fromIdx = current.findIndex(s => s.id === section.id);
    if (fromIdx < 0) return;
    let insertIdx = Number.isFinite(toIndex) ? toIndex : fromIdx;
    insertIdx = Math.max(0, Math.min(insertIdx, current.length));
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    if (fromIdx < insertIdx) insertIdx -= 1;
    next.splice(Math.max(0, Math.min(insertIdx, next.length)), 0, moved);
    const reordered = next.map((s, idx) => ({ ...s, sort_order: idx }));
    setSections(prev => ({
      ...prev,
      [phase]: [
        ...(prev[phase] || []).filter(s => !s.id || s.name === defaultSection),
        ...reordered,
      ].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    }));
    await Promise.all(reordered.map(s => api.patch(`/staff-assignments/sections/${s.id}`, { sort_order: s.sort_order }).catch(() => null)));
  }

  async function moveSectionHere(source, toPhase, toIndex = null) {
    if (!source?.section?.id) return;
    if (source.eventId === eventId && source.phase === toPhase) {
      await reorderSectionInPhase(toPhase, source.section, toIndex);
      return;
    }
    const before = snap();
    pushHistory(before);
    try {
      const { data } = await api.post(`/staff-assignments/sections/${source.section.id}/move`, {
        ...entityParam,
        phase: toPhase,
        sort_order: Number.isFinite(toIndex) ? toIndex : undefined,
      });
      source.removeLocalSection?.(source.phase, source.section.name);
      insertMovedSection(toPhase, data.section, data.assignments || []);
      setActivePhase(toPhase);
    } catch {
      setAssignments(before);
    }
  }

  async function deleteSection(sectionId, phase, sectionName) {
    const current = assignmentsRef.current;
    const sectionItems = current.filter(a => a.phase === phase && (a.section || defaultSection) === sectionName);
    const sectionRecord = (sectionsRef.current[phase] || []).find(s => (
      sectionId ? s.id === sectionId : s.name === sectionName
    ));
    const before = snap();
    pushHistory(before);
    userActionPendingRef.current = true;
    setAssignments(prev => prev.filter(a => !(a.phase === phase && (a.section || defaultSection) === sectionName)));
    sectionItems.forEach(a => {
      if (typeof a.id !== 'number') cancelledTmpRef.current.add(a.id);
    });
    pendingOpsRef.current++;
    try {
      // allSettled: si otra operación (ej. removeAssignment) ya borró alguno de
      // estos items en paralelo, ese 404 individual no debe revertir el borrado.
      await Promise.allSettled(
        sectionItems
          .filter(a => typeof a.id === 'number')
          .map(a => api.delete(`/staff-assignments/${a.id}`))
      );
      if (sectionId) {
        await api.delete(`/staff-assignments/sections/${sectionId}`);
        setDraftSectionIds(prev => {
          if (!prev.has(sectionId)) return prev;
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }
      if (sectionRecord?.kind === 'status' && eventId && ['armado', 'desarmado'].includes(phase)) {
        window.dispatchEvent(new CustomEvent('agenda:aux-operation-deleted', {
          detail: {
            eventId,
            type: phase,
            note: sectionRecord.note || '',
          },
        }));
      }
      setSections(prev => ({
        ...prev,
        [phase]: (prev[phase] || []).filter(s => sectionId ? s.id !== sectionId : s.name !== sectionName),
      }));
      setSectionMenu(null);
    } catch {
      assignmentsRef.current = before;
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  // Un bloque no-default sin personas, título, nota ni hora es un placeholder
  // fantasma solo cuando fue abandonado durante edición. Borrar asignaciones
  // vacía el bloque, pero no lo elimina: para eso existe "Borrar bloque".
  function maybeAutoDeleteEmptySection(phase, sectionName, overrides = {}) {
    if (!sectionName || sectionName === defaultSection) return;
    const section = (sectionsRef.current[phase] || []).find(s => s.name === sectionName);
    if (!section?.id || section.kind === 'status') return;
    if (!draftSectionIds.has(section.id) || !overrides.allowDraft) return;
    const itemsSource = overrides.itemsOverride !== undefined ? overrides.itemsOverride : assignmentsRef.current;
    const hasItems = itemsSource.some(a => a.phase === phase && (a.section || defaultSection) === sectionName);
    if (hasItems) return;
    const title = overrides.title !== undefined ? overrides.title : sectionTitle(section);
    const note = overrides.note !== undefined ? overrides.note : sectionNote(section);
    const time = overrides.time_text !== undefined ? overrides.time_text : sectionTime(section);
    if (String(title || '').trim() || String(note || '').trim() || String(time || '').trim()) return;
    deleteSection(section.id, phase, section.name);
  }

  function cleanupEmptyActiveSection(options = {}) {
    const phase = options.phase || searchPhase;
    const sectionName = options.section || searchSection;
    if (!phase || !sectionName || sectionName === defaultSection) return;
    if (editingSectionMeta || editingSectionNote || renamingSection) return;
    maybeAutoDeleteEmptySection(phase, sectionName, { allowDraft: true });
  }

  async function fetchBookedPersons() {
    if (!entityDate) return;
    try {
      const params = touchPicker ? { date: entityDate } : { date: entityDate, ...excludeParam };
      if (touchPicker) {
        params.detail = 1;
      } else {
        if (startTime) params.start = startTime;
        if (endTime)   params.end   = endTime;
      }
      const { data } = await api.get('/staff-assignments/booked-persons', { params });
      if (Array.isArray(data) && data.some(item => item && typeof item === 'object')) {
        const ids = new Set();
        const labels = new Map();
        const roles = new Map();
        const nameLabels = new Map();
        const nameRoles = new Map();
        const metaById = new Map();
        const metaByName = new Map();
        data.forEach(item => {
          const personId = Number(item.person_id);
          const entityName = String(item.entity_name || item.entity_type || 'otra asignación').trim();
          const statusLabel = `Asignado a ${entityName}`;
          const roleLabel = String(item.role || '').trim();
          const meta = {
            entityType: item.entity_type || '',
            entityId: item.entity_id || null,
            entityName,
            phase: item.phase || '',
            role: roleLabel,
          };
          if (personId) {
            ids.add(personId);
            labels.set(personId, statusLabel);
            roles.set(personId, roleLabel);
            metaById.set(personId, meta);
          }
          const personNameKey = norm(item.person_name || '');
          if (personNameKey) {
            nameLabels.set(personNameKey, statusLabel);
            nameRoles.set(personNameKey, roleLabel);
            metaByName.set(personNameKey, meta);
          }
        });
        setBookedPersonIds(ids);
        setBookedPersonLabels(labels);
        setBookedPersonRoles(roles);
        setBookedPersonNameLabels(nameLabels);
        setBookedPersonNameRoles(nameRoles);
        setBookedPersonMetaById(metaById);
        setBookedPersonMetaByName(metaByName);
      } else {
        setBookedPersonIds(new Set(Array.isArray(data) ? data.map(Number) : []));
        setBookedPersonLabels(new Map());
        setBookedPersonRoles(new Map());
        setBookedPersonNameLabels(new Map());
        setBookedPersonNameRoles(new Map());
        setBookedPersonMetaById(new Map());
        setBookedPersonMetaByName(new Map());
      }
    } catch { /* silencioso */ }
  }

  useEffect(() => {
    if (!touchPicker || touchSheet?.mode !== 'people') return undefined;
    fetchBookedPersons();
    return undefined;
  }, [touchPicker, touchSheet?.mode, touchSheet?.phase, touchSheet?.section, touchSheet?.replaceTarget?.assignmentId, entityDate, assignments]);

  async function refreshConflicts(currentAssignments) {
    if (!entityDate) return;
    const withIds = currentAssignments.filter(a => a.person_id);
    if (!withIds.length) { setConflictKeys(new Set()); return; }
    const params = { date: entityDate, ...excludeParam };
    if (startTime) params.start = startTime;
    if (endTime)   params.end   = endTime;
    const results = await Promise.all(
      withIds.map(a =>
        api.get('/staff-assignments/conflicts', { params: { ...params, person_id: a.person_id } })
          .then(r => r.data.length > 0 ? pillKey(a.phase, a.person_name) : null)
          .catch(() => null)
      )
    );
    setConflictKeys(new Set(results.filter(Boolean)));
  }

  // Sync batch conflict keys from AgendaPage (month navigation / reload)
  useEffect(() => {
    if (externalConflictKeys !== undefined) {
      setConflictKeys(externalConflictKeys instanceof Set ? externalConflictKeys : new Set());
    }
  }, [externalConflictKeys]);

  // Tracks whether the next assignments change was triggered by a user action.
  // When externalConflictKeys is provided, only fire refreshConflicts on user edits
  // (not on initialAssignments sync on mount, which would re-create the N-requests problem).
  const userActionPendingRef = useRef(false);
  useEffect(() => {
    const wasUserAction = userActionPendingRef.current;
    userActionPendingRef.current = false;
    if (!wasUserAction && externalConflictKeys !== undefined) return;
    // Debounce: una ráfaga de ediciones colapsa en un solo fan-out (antes:
    // 1 request por persona en CADA edición → lag al asignar rápido).
    const t = setTimeout(() => refreshConflicts(assignmentsRef.current), 300);
    return () => clearTimeout(t);
  }, [assignments, entityDate, startTime, endTime]);

  function snap() {
    return assignmentsRef.current.map(a => ({ ...a }));
  }

  function pushHistory(currentSnap) {
    setUndoStack(prev => {
      const last = prev[prev.length - 1];
      if (last && JSON.stringify(last) === JSON.stringify(currentSnap)) return prev;
      const next = [...prev, currentSnap];
      return next.length > 80 ? next.slice(-80) : next;
    });
    setRedoStack([]);
  }

  function forPhase(phase, arr = assignmentsRef.current) {
    return arr
      .filter(a => a.phase === phase && !isLegacyOperationalAssignment(a))
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  function personAlias(p) {
    return personnelAlias(p);
  }

  function displayName(item) {
    if (item.person_id) {
      const p = personnel.find(x => x.id === item.person_id);
      if (p) return personAlias(p);
    }
    if (norm(item.person_name) === 'x') return 'Sin cubrir';
    return item.person_name;
  }

  function sectionTitle(section) {
    return getSectionTitle(section, defaultSection);
  }

  function sectionNote(section) {
    return getSectionNote(section);
  }

  function sectionTime(section) {
    return getSectionTime(section);
  }

  function assignmentGroups(items) {
    const groups = new Map();
    for (const item of items) {
      const role = String(item.role || '').trim();
      const key = role
        ? norm(role)
        : `sin-rol-${item.id ?? norm(item.person_name)}`;
      if (!groups.has(key)) groups.set(key, { key, role, items: [] });
      groups.get(key).items.push(item);
    }
    return [...groups.values()];
  }

  function phaseAvailabilityLabel(phase, fallback = currentEntityLabel) {
    if (phase === 'armado') return 'Asignado al armado';
    if (phase === 'desarmado') return 'Asignado al desarmado';
    if (phase === 'evento') return 'Asignado al evento';
    return fallback;
  }

  function phaseAvailabilityKind(phase, fallback = 'current') {
    if (phase === 'armado') return 'current-armado';
    if (phase === 'desarmado') return 'current-desarmado';
    if (phase === 'evento') return 'current-evento';
    return fallback;
  }

  function externalAvailabilityMeta(meta) {
    if (!meta) return { label: '', kind: 'available', role: '' };
    const entityName = String(meta.entityName || meta.entityType || 'otra asignación').trim();
    const label = entityName ? `Asignado a ${entityName}` : 'Asignado a otra actividad';
    return {
      label,
      kind: meta.entityType === 'event' ? 'external-event' : 'external-other',
      role: String(meta.role || '').trim(),
    };
  }

  function options(phase, query = '', section = defaultSection) {
    const sectionItems = forPhase(phase).filter(a => (a.section || defaultSection) === section);
    const assignedIds = new Set(sectionItems.map(a => a.person_id).filter(Boolean));
    const assignedNames = new Set(sectionItems.map(a => norm(a.person_name)));
    const entityAssignedIds = new Set(assignmentsRef.current.map(a => a.person_id).filter(Boolean));
    const entityAssignedNames = new Set(assignmentsRef.current.map(a => norm(a.person_name)).filter(Boolean));
    const replaceTarget = touchPicker && touchSheet?.mode === 'people' ? touchSheet.replaceTarget : null;
    const q = norm(query.trim());

    const matched = personnel
      .map(p => {
        const name = personAlias(p);
        const normalizedName = norm(name);
        const entityAssignment = assignmentsRef.current.find(a => (
          (a.person_id && Number(a.person_id) === Number(p.id))
          || norm(a.person_name) === normalizedName
        ));
        const assignedInSection = assignedIds.has(p.id) || assignedNames.has(normalizedName);
        const assignedInEntity = Boolean(entityAssignment) || entityAssignedIds.has(p.id) || entityAssignedNames.has(normalizedName);
        const isReplaceTarget = replaceTarget && (
          (replaceTarget.personId && Number(replaceTarget.personId) === Number(p.id))
          || norm(replaceTarget.name) === normalizedName
        );
        const externalLabel = bookedPersonLabels.get(p.id) || bookedPersonNameLabels.get(normalizedName) || '';
        const externalRole = bookedPersonRoles.get(p.id) || bookedPersonNameRoles.get(normalizedName) || '';
        const externalMeta = bookedPersonMetaById.get(p.id) || bookedPersonMetaByName.get(normalizedName) || null;
        const externalState = externalAvailabilityMeta(externalMeta);
        const bookedElsewhere = bookedPersonIds.has(p.id) || bookedPersonNameLabels.has(normalizedName);
        const roleLabel = String(assignedInEntity ? (entityAssignment?.role || '') : externalRole).trim();
        const availabilityKind = isReplaceTarget
          ? 'replace'
          : assignedInEntity
            ? phaseAvailabilityKind(entityAssignment?.phase)
            : bookedElsewhere
              ? externalState.kind
              : 'available';
        const bookedLabel = isReplaceTarget
          ? 'Seleccionado'
          : assignedInEntity
            ? phaseAvailabilityLabel(entityAssignment?.phase)
            : (externalState.label || externalLabel);
        return {
          name,
          person_id: p.id,
          formal: true,
          category: roleLabel,
          booked: isReplaceTarget || assignedInEntity || bookedElsewhere,
          bookedLabel,
          availabilityKind,
          isReplaceTarget,
          assignedInEntity,
          assignedInSection,
          disabled: touchPicker && assignedInSection && !isReplaceTarget,
        };
      })
      .filter(c => c.name && (touchPicker || !assignedIds.has(c.person_id)))
      .filter((c, i, arr) => arr.findIndex(x => norm(x.name) === norm(c.name)) === i)
      .filter(c => !q || norm(c.name).includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
      .slice(0, 100);

    // Si no hay match y el usuario escribió algo, ofrecer el nombre como libre (sin person_id)
    if (matched.length === 0 && q) {
      const name = query.trim();
      if (!assignedNames.has(norm(name))) {
        return [{ name, person_id: null, formal: false, external: true }];
      }
      return [];
    }

    return matched;
  }

  function roleOptions(query = '') {
    const q = norm(query.trim());
    const roles = (assignmentModel.quickRoles || [])
      .filter(Boolean)
      .filter((role, i, arr) => arr.findIndex(x => norm(x) === norm(role)) === i)
      .filter(role => !q || norm(role).includes(q))
      .slice(0, 12);
    return roles;
  }

  function findInsertIdx(rects, cx, cy) {
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (cy < r.top + r.height / 2) return i;
      if (cy <= r.bottom && cx < r.right) return i;
    }
    return rects.length;
  }

  async function reorderPhaseSection(phase, sectionName, fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    const before = snap();
    pushHistory(before);
    const all = assignmentsRef.current;
    const sectionItems = all
      .filter(a => a.phase === phase && (a.section || defaultSection) === sectionName)
      .sort((a, b) => a.sort_order - b.sort_order);
    const next = [...sectionItems];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const reordered = next.map((a, i) => ({ ...a, sort_order: i }));
    const others = all.filter(a => !(a.phase === phase && (a.section || defaultSection) === sectionName));
    syncEvent([...others, ...reordered]);
  }

  function computeMenuPosFromRect(rect, optCount) {
    const menuH = Math.min(190, Math.max(34, optCount ? optCount * 26 + 8 : 34));
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUp = spaceBelow < menuH && rect.top > menuH + 8;
    return { left: rect.left, top: openUp ? rect.top - menuH - 5 : rect.bottom + 5, width: Math.max(150, rect.width), openUp };
  }

  function computeMenuPos(inputEl, optCount) {
    return computeMenuPosFromRect(inputEl.getBoundingClientRect(), optCount);
  }

  function openTouchPicker(phase, section = defaultSection) {
    if (!touchPicker) return false;
    setPendingRole(null);
    setActivePhase(phase);
    setSearchPhase(phase);
    setSearchSection(section);
    setSearchQuery('');
    setHighlightIndex(0);
    fetchBookedPersons();
    setMenuPos(null);
    setTouchSheet({ mode: 'people', phase, section });
    return true;
  }

  function openTouchRolePicker({ phase, section = defaultSection, name, currentRole = '', currentPersonId = null, assignmentId = null, source = 'edit' }) {
    if (!touchPicker) return false;
    setPendingRole(null);
    setActivePhase(phase);
    setSearchPhase(phase);
    setSearchSection(section);
    setSearchQuery('');
    setMenuPos(null);
    setTouchSheet({
      mode: 'roles',
      phase,
      section,
      name,
      currentRole,
      currentPersonId,
      assignmentId,
      source,
    });
    return true;
  }

  function openTouchAssignmentActions({ phase, section = defaultSection, name, currentRole = '', currentPersonId = null, assignmentId = null }) {
    if (!touchPicker) return false;
    setActivePhase(phase);
    setSearchPhase(phase);
    setSearchSection(section);
    setSearchQuery('');
    setMenuPos(null);
    setTouchSheet({
      mode: 'actions',
      phase,
      section,
      name,
      currentRole,
      currentPersonId,
      assignmentId,
    });
    return true;
  }

  function openMenu(phase, inputEl, query, section = defaultSection) {
    if (!query.trim()) {
      setMenuPos(null);
      return;
    }
    const opts = options(phase, query, section);
    setMenuPos(computeMenuPos(inputEl, opts.length));
  }

  function closeMenu() {
    cleanupEmptyActiveSection();
    setSearchPhase(null);
    setSearchQuery('');
    setSearchSection(defaultSection);
    setMenuPos(null);
    setPendingRole(null);
    setTouchSheet(null);
    setEditingSectionMeta(null);
    setEditingSectionNote(null);
    setRenamingSection(null);
    setHighlightIndex(0);
    setActivePhase(null);
  }

  useEffect(() => {
    const hasOpenEditor = searchPhase || pendingRole || touchSheet || editingSectionMeta || editingSectionNote || renamingSection;
    if (!hasOpenEditor) return undefined;

    function onDocKeyDown(e) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      document.activeElement?.blur?.();
      closeMenu();
    }

    function onDocPointerDown(e) {
      if (e.target.closest?.('.se-menu, .se-section-menu')) return;
      if (e.target.closest?.('.se-touch-sheet, .se-touch-backdrop')) return;
      if (e.target.closest?.('.se-combo, .se-section-header, .se-section-note, .se-section-note-add, .se-section-note-input')) return;
      if (e.target.closest?.('.se-role-editor')) return;
      closeMenu();
    }

    document.addEventListener('keydown', onDocKeyDown, true);
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onDocKeyDown, true);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
    };
  }, [searchPhase, pendingRole, touchSheet, editingSectionMeta, editingSectionNote, renamingSection, defaultSection]);

  useEffect(() => {
    if (!sectionMenu) return;
    function onDocDown(e) {
      if (e.target.closest?.('.se-section-menu, .se-section-menu-button')) return;
      setSectionMenu(null);
    }
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, [sectionMenu]);

  async function addAssignment(phase, personId, personName, role = '', section = defaultSection) {
    const current = assignmentsRef.current;
    if (current.find(a =>
      a.phase === phase
      && (a.section || defaultSection) === section
      && norm(a.person_name) === norm(personName)
    )) return null;
    const sortOrder = forPhase(phase, current).filter(a => (a.section || defaultSection) === section).length;
    const before = snap();
    const tmp = { id: `tmp-${++tmpSeq}`, phase, person_id: personId, person_name: personName, role, sort_order: sortOrder, section };
    pushHistory(before);
    userActionPendingRef.current = true;
    pendingOpsRef.current++;
    setPreservedEmptySections(prev => {
      const key = sectionRefKey(phase, section);
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    assignmentsRef.current = [...current, tmp];
    setAssignments(prev => [...prev, tmp]);
    if (section !== defaultSection) {
      const sectionId = (sectionsRef.current[phase] || []).find(s => s.name === section)?.id;
      if (sectionId) {
        setDraftSectionIds(prev => {
          if (!prev.has(sectionId)) return prev;
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }
    }
    try {
      const { data } = await api.post('/staff-assignments', {
        ...entityParam, phase, person_id: personId || null, person_name: personName, role, sort_order: sortOrder, section,
      });
      if (cancelledTmpRef.current.has(tmp.id)) {
        cancelledTmpRef.current.delete(tmp.id);
        // El backend upserta por entidad + fase + bloque + nombre: si la
        // persona fue re-agregada, su POST apunta a esta fila viva.
        const reAdded = assignmentsRef.current.some(a =>
          a.phase === phase
          && (a.section || defaultSection) === section
          && norm(a.person_name) === norm(personName)
        );
        if (!reAdded) await api.delete(`/staff-assignments/${data.id}`).catch(() => {});
      } else {
        const latestLocal = assignmentsRef.current.find(a => a.id === tmp.id);
        const merged = latestLocal
          ? {
              ...data,
              person_id: latestLocal.person_id ?? data.person_id,
              person_name: latestLocal.person_name ?? data.person_name,
              role: latestLocal.role ?? data.role,
              section: latestLocal.section ?? data.section,
            }
          : data;
        assignmentsRef.current = assignmentsRef.current.map(a => a.id === tmp.id ? merged : a);
        setAssignments(prev => prev.map(a => a.id === tmp.id ? merged : a));
        const needsPatch = latestLocal && (
          (latestLocal.person_id ?? null) !== (data.person_id ?? null)
          || String(latestLocal.person_name || '') !== String(data.person_name || '')
          || String(latestLocal.role || '') !== String(data.role || '')
        );
        if (needsPatch) {
          await api.put(`/staff-assignments/${data.id}`, {
            person_id: merged.person_id || null,
            person_name: merged.person_name,
            role: merged.role || '',
          }).catch(() => {});
        }
      }
      return assignmentsRef.current.find(a => a.id === data.id) || data;
    } catch {
      cancelledTmpRef.current.delete(tmp.id);
      assignmentsRef.current = before;
      setAssignments(before);
      return null;
    } finally {
      pendingOpsRef.current--;
    }
  }

  useEffect(() => {
    phases.forEach(phase => {
      getSectionsForPhase(phase).forEach(section => {
        staffDropRegistry.set(`${entityKey}::${phase}::${section.name}`, {
          addFn: addAssignment,
          phase,
          section: section.name,
          entityKey,
          clearFn: () => {
            setDropPhase(null);
            resetDropSection();
          },
        });
      });
    });
    return () => {
      phases.forEach(phase => {
        getSectionsForPhase(phase).forEach(section => {
          staffDropRegistry.delete(`${entityKey}::${phase}::${section.name}`);
        });
      });
    };
    // addAssignment/getSectionsForPhase usan refs vivos; esto evita reinstalar
    // handlers globales durante el drag y mantiene destinos estables.
  }, [entityKey, phaseKey, defaultSection, sections]);

  async function removeAssignment(phase, name, sectionName = null) {
    const target = assignmentsRef.current.find(a =>
      a.phase === phase
      && norm(a.person_name) === norm(name)
      && (!sectionName || (a.section || defaultSection) === sectionName)
    );
    if (!target) return;
    const before = snap();
    pushHistory(before);
    userActionPendingRef.current = true;
    ignoreRegressiveInitialUntilRef.current = Date.now() + 3000;
    pendingOpsRef.current++;
    const nextAssignments = assignmentsRef.current.filter(a => !(
      a.phase === phase
      && norm(a.person_name) === norm(name)
      && (!sectionName || (a.section || defaultSection) === sectionName)
    ));
    if (typeof target.id !== 'number') {
      // POST aún en vuelo — marcar para que addAssignment lo borre al recibir el id real
      cancelledTmpRef.current.add(target.id);
      await ensureSectionPersistsWhenEmptied(phase, sectionName || (target.section || defaultSection), nextAssignments);
      if (!nextAssignments.some(a => a.phase === phase && (a.section || defaultSection) === (sectionName || (target.section || defaultSection)))) {
        preserveEmptySection(phase, sectionName || (target.section || defaultSection));
      }
      writeStableAssignments(entityKey, nextAssignments);
      assignmentsRef.current = nextAssignments;
      setAssignments(nextAssignments);
      try {
        await syncEvent(nextAssignments);
      } finally {
        pendingOpsRef.current--;
      }
      return;
    }
    try {
      await api.delete(`/staff-assignments/${target.id}`).catch(async () => {
        await syncEvent(nextAssignments);
      });
      await ensureSectionPersistsWhenEmptied(phase, sectionName || (target.section || defaultSection), nextAssignments);
      if (!nextAssignments.some(a => a.phase === phase && (a.section || defaultSection) === (sectionName || (target.section || defaultSection)))) {
        preserveEmptySection(phase, sectionName || (target.section || defaultSection));
      }
      writeStableAssignments(entityKey, nextAssignments);
      assignmentsRef.current = nextAssignments;
      setAssignments(nextAssignments);
    } catch {
      assignmentsRef.current = before;
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  async function clearSectionAssignments(phase, sectionName) {
    const sectionItems = assignmentsRef.current.filter(a => a.phase === phase && (a.section || defaultSection) === sectionName);
    if (!sectionItems.length) {
      setSectionMenu(null);
      return;
    }
    const before = snap();
    pushHistory(before);
    userActionPendingRef.current = true;
    const nextAssignments = assignmentsRef.current.filter(a => !(a.phase === phase && (a.section || defaultSection) === sectionName));
    pendingOpsRef.current++;
    await ensureSectionPersistsWhenEmptied(phase, sectionName, nextAssignments);
    preserveEmptySection(phase, sectionName);
    assignmentsRef.current = nextAssignments;
    setAssignments(nextAssignments);
    sectionItems.forEach(a => {
      if (typeof a.id !== 'number') cancelledTmpRef.current.add(a.id);
    });
    try {
      const deletes = await Promise.allSettled(
        sectionItems
          .filter(a => typeof a.id === 'number')
          .map(a => api.delete(`/staff-assignments/${a.id}`))
      );
      if (deletes.some(result => result.status === 'rejected') || sectionItems.some(a => typeof a.id !== 'number')) {
        await syncEvent(nextAssignments);
      }
      setSectionMenu(null);
    } catch {
      assignmentsRef.current = before;
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  async function updateAssignmentDetails(phase, name, patch, sectionName = null, assignmentId = null) {
    const fallbackMatches = a =>
      a.phase === phase
      && norm(a.person_name) === norm(name)
      && (!sectionName || (a.section || defaultSection) === sectionName);
    const hasAssignmentId = assignmentId !== null && assignmentId !== undefined;
    const target = hasAssignmentId
      ? assignmentsRef.current.find(a => a.id === assignmentId) || assignmentsRef.current.find(fallbackMatches)
      : assignmentsRef.current.find(fallbackMatches);
    if (!target) return;
    const targetId = target.id;
    const hasTargetId = targetId !== null && targetId !== undefined;
    const matchesTarget = a => hasTargetId ? a.id === targetId : fallbackMatches(a);
    const before = snap();
    pushHistory(before);
    const nextName = patch.person_name !== undefined ? patch.person_name : target.person_name;
    const nextPersonId = patch.person_id !== undefined ? patch.person_id : target.person_id;
    const nextRole = patch.role !== undefined ? patch.role : target.role;
    assignmentsRef.current = assignmentsRef.current.map(a => (
      matchesTarget(a) ? { ...a, person_id: nextPersonId || null, person_name: nextName, role: nextRole } : a
    ));
    setAssignments(prev => prev.map(a => (
      matchesTarget(a) ? { ...a, person_id: nextPersonId || null, person_name: nextName, role: nextRole } : a
    )));
    if (typeof target.id !== 'number') return;
    pendingOpsRef.current++;
    try {
      await api.put(`/staff-assignments/${target.id}`, {
        person_id: nextPersonId || null,
        person_name: nextName,
        role: nextRole,
      });
    } catch {
      assignmentsRef.current = before;
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  async function updateRole(phase, name, role) {
    updateAssignmentDetails(phase, name, { role });
  }

  function openRoleEditor({ phase, section, name, currentRole = '', currentPersonId = null, assignmentId = null, anchorEl = null, anchorRect = null }) {
    if (touchPicker) {
      openTouchRolePicker({ phase, section, name, currentRole, currentPersonId, assignmentId, source: 'edit' });
      return;
    }
    const refKey = `${phase}::${section}`;
    const roleQuery = currentRole || '';
    setPendingRole({ phase, section, name, currentRole: roleQuery, currentPersonId, assignmentId });
    setSearchPhase(phase);
    setSearchSection(section);
    setSearchQuery(touchPicker ? '' : roleQuery);
    setHighlightIndex(0);
    if (touchPicker && (anchorEl || anchorRect)) {
      const rect = anchorRect || anchorEl.getBoundingClientRect();
      setMenuPos(computeMenuPosFromRect(rect, roleOptions('').length + 1));
      return;
    }
    setMenuPos(null);
    setTimeout(() => {
      const input = searchRefs.current[refKey];
      if (!input) return;
      input.focus({ preventScroll: true });
      input.select?.();
      setMenuPos(computeMenuPos(input, roleOptions(roleQuery).length));
    }, 0);
  }

  async function moveAssignment(fromPhase, toPhase, name) {
    if (fromPhase === toPhase) return;
    const current = assignmentsRef.current;
    const source = current.find(a => a.phase === fromPhase && norm(a.person_name) === norm(name));
    if (!source) return;
    if (current.find(a => a.phase === toPhase && norm(a.person_name) === norm(name))) return;
    const before = snap();
    pushHistory(before);
    const newOrder = forPhase(toPhase, current).length;
    userActionPendingRef.current = true;
    setAssignments(prev => prev
      .filter(a => !(a.phase === fromPhase && norm(a.person_name) === norm(name)))
      .concat([{ ...source, phase: toPhase, sort_order: newOrder }])
    );
    if (typeof source.id !== 'number') return;
    // pendingOps: cierra la ventana en la que un refetch del padre revertiría
    // el movimiento optimista mientras el PUT está en vuelo.
    pendingOpsRef.current++;
    try {
      await api.put(`/staff-assignments/${source.id}`, { phase: toPhase, sort_order: newOrder });
    } catch {
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  async function moveAssignmentToSection(fromPhase, fromSection, toPhase, toSection, name) {
    if (fromPhase === toPhase && (fromSection || defaultSection) === (toSection || defaultSection)) return;
    const current = assignmentsRef.current;
    const source = current.find(a =>
      a.phase === fromPhase
      && norm(a.person_name) === norm(name)
      && (a.section || defaultSection) === (fromSection || defaultSection)
    );
    if (!source) return;
    if (current.find(a =>
      a.phase === toPhase
      && norm(a.person_name) === norm(name)
      && (a.section || defaultSection) === (toSection || defaultSection)
    )) return;
    const before = snap();
    pushHistory(before);
    const newOrder = forPhase(toPhase, current)
      .filter(a => (a.section || defaultSection) === (toSection || defaultSection))
      .length;
    userActionPendingRef.current = true;
    setAssignments(prev => prev.map(a =>
      (source.id !== undefined
        ? a.id === source.id
        : a.phase === fromPhase
          && norm(a.person_name) === norm(name)
          && (a.section || defaultSection) === (fromSection || defaultSection))
        ? { ...a, phase: toPhase, section: toSection || defaultSection, sort_order: newOrder }
        : a
    ));
    if (typeof source.id !== 'number') return;
    pendingOpsRef.current++;
    try {
      await api.put(`/staff-assignments/${source.id}`, {
        phase: toPhase,
        section: toSection || defaultSection,
        sort_order: newOrder,
      });
    } catch {
      setAssignments(before);
    } finally {
      pendingOpsRef.current--;
    }
  }

  async function copyAssignment(fromPhase, toPhase, name, role) {
    const source = assignmentsRef.current.find(a => a.phase === fromPhase && norm(a.person_name) === norm(name));
    if (!source) return;
    await addAssignment(toPhase, source.person_id, source.person_name, role ?? source.role);
  }

  async function syncEvent(targetAssignments) {
    userActionPendingRef.current = true;
    assignmentsRef.current = targetAssignments;
    setAssignments(targetAssignments);
    const singleEntityType = taskId ? 'task' : armadoId ? 'armado' : operationId ? 'operation' : null;
    const singleEntityId = taskId || armadoId || operationId || null;
    try {
      if (singlePhase && singleEntityType && singleEntityId) {
        await api.post('/staff-assignments/sync-entity', {
          entity_type: singleEntityType,
          entity_id: singleEntityId,
          assignments: targetAssignments,
        });
      } else {
        await api.post('/staff-assignments/sync-event', { event_id: eventId, assignments: targetAssignments });
      }
    } catch { /* silencioso */ }
  }

  function undo() {
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const previous = prev[prev.length - 1];
      const current = snap();
      setRedoStack(r => [...r, current]);
      syncEvent(previous);
      return prev.slice(0, -1);
    });
  }

  function redo() {
    setRedoStack(prev => {
      if (!prev.length) return prev;
      const next = prev[prev.length - 1];
      const current = snap();
      setUndoStack(u => [...u, current]);
      syncEvent(next);
      return prev.slice(0, -1);
    });
  }

  // Global keyboard handler
  useEffect(() => {
    function onKeyDown(e) {
      if (!canEdit) return;
      const isShortcut = e.metaKey || e.ctrlKey;
      if (!isShortcut) return;
      const k = e.key.toLowerCase();

      const focusInEditor = Object.values(lineRefs.current).some(el => el?.contains(document.activeElement))
        || false;

      if (k === 'z' && !e.shiftKey && focusInEditor) {
        e.preventDefault();
        undo();
        return;
      }
      if ((k === 'z' && e.shiftKey || k === 'y') && focusInEditor) {
        e.preventDefault();
        redo();
        return;
      }
      if (k === 'c' && selectedKeys.size > 0) {
        e.preventDefault();
        setClipboard(assignmentsRef.current.filter(a => selectedKeys.has(pillKey(a.phase, a.person_name))));
        return;
      }
      if (k === 'v' && clipboard.length > 0 && activePhase) {
        e.preventDefault();
        const before = snap();
        pushHistory(before);
        clipboard.forEach(item => addAssignment(activePhase, item.person_id, item.person_name, item.role));
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [canEdit, selectedKeys, clipboard, activePhase]);

  // Pointer drag handlers
  useEffect(() => {
    function onMove(e) {
      const section = sectionDragRef.current;
      if (section) {
        const dist = Math.hypot(e.clientX - section.startX, e.clientY - section.startY);
        if (!section.dragging && dist < 6) return;
        if (!section.dragging) {
          section.dragging = true;
          section.block?.classList.add('se-section--dragging');
          const ghost = document.createElement('div');
          ghost.className = 'se-ghost se-ghost--section';
          ghost.textContent = section.label || assignmentModel.labels.section || 'Bloque';
          ghost.style.left = `${e.clientX + 12}px`;
          ghost.style.top = `${e.clientY - 10}px`;
          document.body.appendChild(ghost);
          section.ghost = ghost;
        }
        if (section.ghost) {
          section.ghost.style.left = `${e.clientX + 12}px`;
          section.ghost.style.top = `${e.clientY - 10}px`;
        }
        const lineEl = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.se-line');
        const targetPhase = lineEl?.dataset?.phase;
        if (targetPhase && phases.includes(targetPhase)) {
          const sectionEls = Array.from(lineEl.querySelectorAll('.se-section--framed[data-section-id]'));
          let insertIndex = sectionEls.length;
          for (let i = 0; i < sectionEls.length; i++) {
            const rect = sectionEls[i].getBoundingClientRect();
            if (e.clientY < rect.top + rect.height / 2) {
              insertIndex = i;
              break;
            }
          }
          section.targetPhase = targetPhase;
          section.targetIndex = insertIndex;
          dropPhaseRef.current = targetPhase;
          sectionDropTarget = { eventId, phase: targetPhase, index: insertIndex, moveFn: moveSectionHere, clearFn: () => setDropPhase(null) };
          setDropPhase(targetPhase);
          const marker = { phase: targetPhase, index: insertIndex };
          sectionDropMarkerRef.current = marker;
          setSectionDropMarker(marker);
        }
        return;
      }

      const drag = dragRef.current;
      if (!drag) return;
      const dist = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
      if (!drag.dragging && dist < 6) return;
      if (!drag.dragging) {
        drag.dragging = true;
        drag.pill?.classList.add('se-pill--dragging');
        const ghost = document.createElement('div');
        ghost.className = 'se-ghost';
        ghost.textContent = drag.name + (drag.role ? ' · ' + drag.role : '');
        ghost.style.left = `${e.clientX + 12}px`;
        ghost.style.top = `${e.clientY - 10}px`;
        document.body.appendChild(ghost);
        drag.ghost = ghost;
      }
      if (drag.ghost) {
        drag.ghost.style.left = `${e.clientX + 12}px`;
        drag.ghost.style.top = `${e.clientY - 10}px`;
      }
      drag.copy = e.metaKey || e.ctrlKey;
      document.documentElement.classList.toggle('se-copy-mode', drag.copy);

      // Reorder animation within same phase
      if (drag.eventId === eventId && drag.pillRects) {
        const lineEl = lineRefs.current[drag.phase];
        if (lineEl) {
          const lr = lineEl.getBoundingClientRect();
          const inside = e.clientX >= lr.left && e.clientX <= lr.right && e.clientY >= lr.top && e.clientY <= lr.bottom;
          if (inside) {
            const toIdx = findInsertIdx(drag.pillRects, e.clientX, e.clientY);
            const clamped = Math.min(toIdx, drag.pillRects.length - 1);
            if (clamped !== drag.currentToIdx) {
              drag.currentToIdx = clamped;
              const val = { phase: drag.phase, section: drag.section || defaultSection, fromIdx: drag.fromIdx, toIdx: clamped, rects: drag.pillRects };
              dragReorderRef.current = val;
              setDragReorder(val);
            }
          } else {
            if (dragReorderRef.current) {
              dragReorderRef.current = null;
              setDragReorder(null);
            }
          }
        }
      }
    }
    function onUp(e) {
      const section = sectionDragRef.current;
      if (section) {
        sectionDragRef.current = null;
        sectionDrag = null;
        if (section.dragging) {
          section.ghost?.remove();
          section.block?.classList.remove('se-section--dragging');
          suppressSectionClickRef.current = true;
          setTimeout(() => { suppressSectionClickRef.current = false; }, 0);
          const target = sectionDropTarget;
          sectionDropTarget = null;
          dropPhaseRef.current = null;
          setDropPhase(null);
          resetDropSection();
          sectionDropMarkerRef.current = null;
          setSectionDropMarker(null);
          if (target) {
            target.clearFn?.();
            target.moveFn(section, target.phase, target.index);
          }
        }
        return;
      }

      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      staffDrag = null;
      const reorder = dragReorderRef.current;
      dragReorderRef.current = null;
      setDragReorder(null);
      if (drag.dragging) {
        drag.ghost?.remove();
        drag.pill?.classList.remove('se-pill--dragging');
        document.documentElement.classList.remove('se-copy-mode');
        const pointTarget = document
          .elementFromPoint(e.clientX, e.clientY)
          ?.closest?.('[data-staff-drop-entity][data-staff-drop-phase][data-staff-drop-section]');
        const pointEntity = pointTarget?.dataset.staffDropEntity;
        const pointPhase = pointTarget?.dataset.staffDropPhase;
        const pointSection = pointTarget?.dataset.staffDropSection;
        const pointRegistryTarget = pointEntity && pointPhase && pointSection
          ? staffDropRegistry.get(`${pointEntity}::${pointPhase}::${pointSection}`)
          : null;
        const targetPhase = pointPhase || dropPhaseRef.current;
        const crossTarget = pointRegistryTarget || staffDropTarget;
        staffDropTarget = null;
        dropPhaseRef.current = null;
        setDropPhase(null);
        resetDropSection();
        const isCopy = drag.copy || e.metaKey || e.ctrlKey;
        if (crossTarget && crossTarget.entityKey !== drag.entityKey) {
          crossTarget.clearFn?.();
          crossTarget.addFn(crossTarget.phase, drag.person_id, drag.name, drag.role, crossTarget.section || defaultSection);
          if (!isCopy) removeAssignment(drag.phase, drag.name, drag.section || defaultSection);
        } else if (crossTarget && crossTarget.entityKey === drag.entityKey) {
          const targetSection = crossTarget.section || defaultSection;
          if (targetPhase === drag.phase && targetSection === (drag.section || defaultSection) && reorder && reorder.fromIdx !== reorder.toIdx) {
            reorderPhaseSection(drag.phase, drag.section || defaultSection, reorder.fromIdx, reorder.toIdx);
          } else {
            if (isCopy) copyAssignment(drag.phase, targetPhase, drag.name, drag.role);
            else moveAssignmentToSection(drag.phase, drag.section || defaultSection, targetPhase, targetSection, drag.name);
          }
        } else if (targetPhase && drag.entityKey === entityKey) {
          if (targetPhase === drag.phase && reorder && reorder.fromIdx !== reorder.toIdx) {
            reorderPhaseSection(drag.phase, drag.section || defaultSection, reorder.fromIdx, reorder.toIdx);
          } else if (targetPhase !== drag.phase) {
            if (isCopy) copyAssignment(drag.phase, targetPhase, drag.name);
            else moveAssignment(drag.phase, targetPhase, drag.name);
          }
        }
      }
    }
    function onCancel() {
      if (sectionDragRef.current?.ghost) sectionDragRef.current.ghost.remove();
      if (sectionDragRef.current?.block) sectionDragRef.current.block.classList.remove('se-section--dragging');
      sectionDragRef.current = null;
      sectionDrag = null;
      sectionDropTarget = null;
      sectionDropMarkerRef.current = null;
      setSectionDropMarker(null);
      if (dragRef.current?.ghost) dragRef.current.ghost.remove();
      if (dragRef.current?.pill) dragRef.current.pill.classList.remove('se-pill--dragging');
      dragRef.current = null;
      staffDrag = null;
      dragReorderRef.current = null;
      setDragReorder(null);
      setDropPhase(null);
      resetDropSection();
      dropPhaseRef.current = null;
      document.documentElement.classList.remove('se-copy-mode');
    }
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
    // Sin `assignments` en deps: el cuerpo usa assignmentsRef.current (siempre
    // al día), así no re-registramos los listeners globales en cada edición.
  }, [eventId, taskId, armadoId]);

  // Resalta el bloque exacto bajo el cursor (no solo la fase completa) mientras
  // se arrastra una persona o un bloque. No-op fuera de un drag activo.
  function setDropSection(phase, sectionName) {
    if (!dragRef.current?.dragging && !sectionDragRef.current?.dragging) return;
    const key = `${phase}::${sectionName}`;
    if (dropSectionKeyRef.current === key) return;
    dropSectionKeyRef.current = key;
    setDropSectionKey(key);
    if (dragRef.current?.dragging) {
      staffDropTarget = staffDropRegistry.get(`${entityKey}::${phase}::${sectionName}`) || {
        addFn: addAssignment,
        phase,
        section: sectionName,
        entityKey,
        clearFn: () => {
          setDropPhase(null);
          resetDropSection();
        },
      };
      dropPhaseRef.current = phase;
      setDropPhase(phase);
    }
  }

  function clearDropSection(phase, sectionName) {
    const key = `${phase}::${sectionName}`;
    if (dropSectionKeyRef.current !== key) return;
    dropSectionKeyRef.current = null;
    setDropSectionKey(null);
    if (staffDropTarget?.entityKey === entityKey && staffDropTarget?.phase === phase && staffDropTarget?.section === sectionName) {
      staffDropTarget = null;
    }
  }

  function resetDropSection() {
    dropSectionKeyRef.current = null;
    setDropSectionKey(null);
  }

  function handlePillPointerDown(e, phase, sectionName, name, role, pillEl) {
    if (e.target.closest('.se-remove')) return;
    const pillEls = Array.from(pillEl.closest('.se-pills')?.querySelectorAll('.se-pill') ?? []);
    const fromIdx = pillEls.indexOf(pillEl);
    const pillRects = pillEls.map(el => el.getBoundingClientRect());
    const current = assignmentsRef.current.find(a => (
      a.phase === phase &&
      norm(a.person_name) === norm(name) &&
      (a.section || defaultSection) === (sectionName || defaultSection)
    ));
    const drag = {
      phase,
      section: sectionName,
      name,
      role,
      person_id: current?.person_id || null,
      entityKey,
      eventId,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      copy: false,
      pill: pillEl,
      fromIdx,
      pillRects,
      currentToIdx: fromIdx,
    };
    dragRef.current = drag;
    staffDrag = drag;
    dropPhaseRef.current = phase;
    setDropPhase(phase);
  }

  function handleSectionPointerDown(e, phase, section, handleEl) {
    if (!section?.id || section.name === defaultSection) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (!e.target.closest?.('.se-section-drag-handle')) return;
    e.preventDefault();
    e.stopPropagation();
    const label = sectionTitle(section) || sectionNote(section) || assignmentModel.labels.section || 'Bloque';
    const blockEl = handleEl.closest?.('.se-section') || null;
    const drag = {
      phase,
      section,
      eventId,
      label,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      block: blockEl,
      removeLocalSection,
    };
    sectionDragRef.current = drag;
    sectionDrag = drag;
    sectionDropTarget = { eventId, phase, moveFn: moveSectionHere, clearFn: () => setDropPhase(null) };
    dropPhaseRef.current = phase;
    setDropPhase(phase);
  }

  function handlePillClick(e, phase, name, assignmentId = null) {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      const k = pillKey(phase, name);
      setSelectedKeys(prev => {
        const next = new Set(prev);
        next.has(k) ? next.delete(k) : next.add(k);
        return next;
      });
      setActivePhase(phase);
      return;
    }
    if (dragRef.current?.dragging) return;
    setSelectedKeys(new Set());
    const pillEl = e.currentTarget;
    const section = pillEl.closest?.('.se-section')?.dataset?.sectionName || defaultSection;
    const current = assignmentId !== null && assignmentId !== undefined
      ? assignmentsRef.current.find(a => a.id === assignmentId) || assignmentsRef.current.find(a =>
          a.phase === phase
          && norm(a.person_name) === norm(name)
          && (a.section || defaultSection) === section
        )
      : assignmentsRef.current.find(a =>
      a.phase === phase
      && norm(a.person_name) === norm(name)
      && (a.section || defaultSection) === section
    );
    openRoleEditor({
      phase,
      section,
      name,
      currentRole: current?.role || '',
      currentPersonId: current?.person_id || null,
      assignmentId: current?.id ?? null,
      anchorEl: e.currentTarget,
    });
  }

  async function selectOption(phase, section, opt, anchorEl = null) {
    const anchorRect = anchorEl?.getBoundingClientRect?.() || null;
    setMenuPos(null);
    const result = await addAssignment(phase, opt.person_id, opt.name, '', section);
    const added = result || assignmentsRef.current.find(a =>
      a.phase === phase
      && (a.section || defaultSection) === section
      && norm(a.person_name) === norm(opt.name)
    );
    openRoleEditor({
      phase,
      section,
      name: added?.person_name ?? opt.name,
      currentRole: added?.role || '',
      currentPersonId: added?.person_id || opt.person_id || null,
      assignmentId: added?.id ?? null,
      anchorRect,
    });
  }

  async function selectTouchPerson(opt) {
    if (!touchSheet) return;
    if (opt?.disabled) return;
    const { phase, section } = touchSheet;
    if (touchSheet.replaceTarget) {
      await updateAssignmentDetails(phase, touchSheet.replaceTarget.name, {
        person_id: opt.person_id || null,
        person_name: opt.name,
      }, section, touchSheet.replaceTarget.assignmentId);
      const updated = assignmentsRef.current.find(a =>
        (a.id === touchSheet.replaceTarget.assignmentId)
        || (a.phase === phase && (a.section || defaultSection) === section && norm(a.person_name) === norm(opt.name))
      );
      setTouchSheet({
        mode: 'roles',
        phase,
        section,
        name: updated?.person_name || opt.name,
        currentRole: updated?.role || '',
        currentPersonId: updated?.person_id || opt.person_id || null,
        assignmentId: updated?.id ?? touchSheet.replaceTarget.assignmentId ?? null,
        source: 'edit',
      });
      return;
    }

    const result = await addAssignment(phase, opt.person_id, opt.name, '', section);
    const added = result || assignmentsRef.current.find(a =>
      a.phase === phase
      && (a.section || defaultSection) === section
      && norm(a.person_name) === norm(opt.name)
    );
    setTouchSheet({
      mode: 'roles',
      phase,
      section,
      name: added?.person_name || opt.name,
      currentRole: added?.role || '',
      currentPersonId: added?.person_id || opt.person_id || null,
      assignmentId: added?.id ?? null,
      source: 'add',
    });
  }

  async function finishTouchRole(roleText = '') {
    if (!touchSheet || touchSheet.mode !== 'roles') return;
    const normalizedRole = String(roleText || '').trim();
    await updateAssignmentDetails(touchSheet.phase, touchSheet.name, {
      person_id: touchSheet.currentPersonId || null,
      role: normalizedRole,
    }, touchSheet.section, touchSheet.assignmentId);
    if (touchSheet.source === 'add') {
      setTouchSheet({ mode: 'people', phase: touchSheet.phase, section: touchSheet.section });
    } else {
      setTouchSheet(null);
      setSearchPhase(null);
      setSearchSection(defaultSection);
      setActivePhase(null);
    }
  }

  async function removeTouchAssignment() {
    if (!touchSheet || touchSheet.mode !== 'actions') return;
    await removeAssignment(touchSheet.phase, touchSheet.name, touchSheet.section);
    setTouchSheet(null);
    setSearchPhase(null);
    setActivePhase(null);
  }

  function touchAssignedItems() {
    if (!touchSheet?.phase) return [];
    const section = touchSheet.section || defaultSection;
    return forPhase(touchSheet.phase, assignmentsRef.current)
      .filter(a => (a.section || defaultSection) === section);
  }

  function touchRoleOptions() {
    return [
      '',
      ...roleOptions('').filter((role, i, arr) => arr.findIndex(x => norm(x) === norm(role)) === i),
    ];
  }

  function touchPeopleOptions() {
    if (!touchSheet?.phase) return [];
    return options(touchSheet.phase, '', touchSheet.section || defaultSection);
  }

  function finishPendingRole(roleText = searchQuery) {
    if (!pendingRole) return;
    const normalizedRole = String(roleText || '').trim();
    if (normalizedRole || pendingRole.currentRole) {
      updateAssignmentDetails(pendingRole.phase, pendingRole.name, {
        person_id: pendingRole.currentPersonId || null,
        role: normalizedRole ? normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1) : '',
      }, pendingRole.section, pendingRole.assignmentId);
    }
    const { phase, section } = pendingRole;
    setPendingRole(null);
    setSearchPhase(phase);
    setSearchSection(section);
    setSearchQuery('');
    setMenuPos(null);
    setTimeout(() => searchRefs.current[`${phase}::${section}`]?.focus(), 30);
  }

  const isRoleSearch = Boolean(searchPhase && pendingRole?.phase === searchPhase && pendingRole?.section === searchSection);
  const menuOptions = searchPhase && !isRoleSearch ? options(searchPhase, searchQuery, searchSection) : [];
  const roleMenuOptions = isRoleSearch ? roleOptions(searchQuery) : [];
  const activeMenuIndex = menuOptions.length ? Math.min(highlightIndex, menuOptions.length - 1) : 0;
  const activeRoleIndex = roleMenuOptions.length ? Math.min(highlightIndex, roleMenuOptions.length - 1) : 0;

  useEffect(() => {
    if (!isRoleSearch || !pendingRole) return;
    const refKey = `${pendingRole.phase}::${pendingRole.section}`;
    if (touchPicker) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const input = searchRefs.current[refKey];
      if (!input) return;
      if (document.activeElement !== input) input.focus({ preventScroll: true });
      setMenuPos(computeMenuPos(input, roleOptions(searchQuery).length));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isRoleSearch, pendingRole, searchQuery, touchPicker]);

  return (
    <div ref={rootRef} className={singlePhase ? 'se-cell se-cell--single' : 'se-cell se-cell--event'}>
      {phases.map(phase => {
        const isDropTarget = dropPhase === phase;
        const phaseSections = modelUnavailable ? [] : getSectionsForPhase(phase);
        const phaseItems = modelUnavailable ? [] : forPhase(phase, assignments);
        const phaseCount = phaseItems.length;
        const dr = dragReorder;

        const line = (
          <div
            key={phase}
            ref={el => lineRefs.current[phase] = el}
            data-phase={phase}
            className={`se-line se-line--${phase}${isDropTarget ? ' se-line--drop' : ''}`}
            onPointerEnter={() => {
              if (staffDrag) {
                setDropPhase(phase);
                dropPhaseRef.current = phase;
                if (staffDrag.eventId !== eventId) {
                  staffDropTarget = { addFn: addAssignment, phase, eventId, clearFn: () => { setDropPhase(null); resetDropSection(); } };
                }
              }
              if (sectionDrag) {
                setDropPhase(phase);
                dropPhaseRef.current = phase;
                sectionDropTarget = { moveFn: moveSectionHere, phase, eventId, index: null, clearFn: () => { setDropPhase(null); resetDropSection(); } };
              }
            }}
            onPointerLeave={() => {
              if (dropPhaseRef.current === phase) {
                setDropPhase(null);
                dropPhaseRef.current = null;
                resetDropSection();
                if (staffDropTarget?.eventId === eventId) staffDropTarget = null;
                if (sectionDropTarget?.eventId === eventId) sectionDropTarget = null;
                sectionDropMarkerRef.current = null;
                setSectionDropMarker(null);
              }
            }}
            onClick={() => {
              if (modelUnavailable) return;
              setActivePhase(phase);
              if (canEdit) {
                setSearchPhase(phase);
                setSearchSection(defaultSection);
                setTimeout(() => searchRefs.current[`${phase}::${defaultSection}`]?.focus(), 0);
              }
            }}
          >
            {modelUnavailable ? (
              // Sin role="alert": este bloque se repite por cada fase de cada
              // fila, y un live region por celda haría que el lector de
              // pantalla anuncie el mismo error decenas de veces de golpe.
              // El texto queda visible y el botón es alcanzable por foco.
              <div className="se-error">
                <span className="se-error-text">No se pudo cargar el personal</span>
                {retryAssignmentModel && (
                  <button
                    type="button"
                    className="se-error-retry"
                    disabled={modelRetrying}
                    onClick={e => { e.stopPropagation(); handleRetryAssignmentModel(); }}
                  >
                    {modelRetrying ? 'Reintentando…' : 'Reintentar'}
                  </button>
                )}
              </div>
            ) : (
            <>
            <div className="se-phase-header">
              <span className="se-phase-name">{phaseLabels[phase] || FALLBACK_PHASE_LABELS[phase] || phase}</span>
              <span className="se-phase-count">{phaseCount ? `${phaseCount} pers.` : assignmentModel.labels.empty}</span>
            </div>
            {phaseSections.map((section, sectionIndex) => {
              const sectionItems = phaseItems.filter(a => (a.section || defaultSection) === section.name);
              const isSearchActive = searchPhase === phase && searchSection === section.name;
              const isRenamingSection = renamingSection?.phase === phase && (
                renamingSection?.sectionId
                  ? renamingSection.sectionId === section.id
                  : renamingSection?.oldName === section.name
              );
              const isEditingSectionMeta = editingSectionMeta?.phase === phase && editingSectionMeta?.sectionName === section.name;
              const isEditingSectionNote = Boolean(section.id) && editingSectionNote === section.id;
              const isSectionSelected = activePhase === phase && searchSection === section.name;
              const isPreservedEmptySection = preservedEmptySections.has(sectionRefKey(phase, section.name));
              const sectionView = getSectionViewState({
                section,
                sectionItems,
                defaultSection,
                canEdit,
                isSearchActive,
                isRenamingSection,
                isEditingSectionMeta,
                isEditingSectionNote,
                isSectionSelected,
                isPreservedEmptySection,
              });
              const refKey = `${phase}::${section.name}`;
              const menuOpen = sectionMenu?.phase === phase && sectionMenu?.sectionName === section.name;
              const groupedAssignments = assignmentGroups(sectionItems);

              return (
                <AssignmentBlock
                  key={section.name}
                  phase={phase}
                  section={section}
                  sectionIndex={sectionIndex}
                  sectionItems={sectionItems}
                  groupedAssignments={groupedAssignments}
                  shouldRenderSection={sectionView.shouldRenderSection}
                  showSectionHeader={sectionView.showSectionHeader}
                  isSectionActive={sectionView.isSectionActive}
                  isStatusOnly={sectionView.isStatusOnly}
                  isSearchActive={isSearchActive}
                  isRenamingSection={isRenamingSection}
                  isEditingSectionMeta={isEditingSectionMeta}
                  isEditingSectionNote={isEditingSectionNote}
                  hasSectionMeta={sectionView.hasSectionMeta}
                  canEdit={canEdit}
                  canEditSectionMeta={sectionView.canEditSectionMeta}
                  canMoveDeleteSection={sectionView.canMoveDeleteSection}
                  canDeleteWholeSection={sectionView.canDeleteWholeSection}
                  isFramed={sectionView.isFramed}
                  isEmptyNamedSection={sectionView.isEmptyNamedSection}
                  menuOpen={menuOpen}
                  displaySectionTitle={sectionView.displaySectionTitle}
                  displaySectionNote={sectionView.displaySectionNote}
                  displaySectionTime={sectionView.displaySectionTime}
                  showAssignmentSearch={sectionView.showAssignmentSearch}
                  isDropTarget={dropSectionKey === refKey}
                  refKey={refKey}
                  sectionDropMarker={sectionDropMarker}
                  defaultSection={defaultSection}
                  assignmentModel={assignmentModel}
                  pendingRole={pendingRole}
                  searchQuery={searchQuery}
                  selectedKeys={selectedKeys}
                  conflictKeys={conflictKeys}
                  dragReorder={dr}
                  ctx={{
                    advancePhaseTab,
                    createSection,
                    onCreateAuxOperation,
                    singlePhase,
                    beginSectionMetaEdit,
                    beginSectionNoteEdit,
                    clearDropSection,
                    clearSectionAssignments,
                    closeMenu,
                    computeMenuPos,
                    deleteSection,
                    displayName,
                    fetchBookedPersons,
                    finishPendingRole,
                    focusSectionMeta,
                    handlePillClick,
                    handlePillPointerDown,
                    handleSectionPointerDown,
                    handleStatusSectionAction,
                    highlightIndex,
                    maybeAutoDeleteEmptySection,
                    menuOptions,
                    normalizeTimeText,
                    openMenu,
                    openTouchPicker,
                    openTouchAssignmentActions,
                    openTouchRolePicker,
                    options,
                    patchSection,
                    patchSectionMeta,
                    pillKey,
                    removeAssignment,
                    renameSection,
                    roleOptions,
                    rootRef,
                    entityKey,
                    searchRefs,
                    sectionTimeRefs,
                    sectionTitleRefs,
                    selectOption,
                    setActivePhase,
                    setDropSection,
                    setEditingSectionMeta,
                    setEditingSectionNote,
                    setHighlightIndex,
                    setMenuPos,
                    setPendingRole,
                    setRenamingSection,
                    setSearchPhase,
                    setSearchQuery,
                    setSearchSection,
                    setSectionMenu,
                    stopSectionChromeEvent,
                    suppressSectionClickRef,
                    touchPicker,
                  }}
                />
              );
            })}
            {sectionDropMarker?.phase === phase && sectionDropMarker.index === phaseSections.length && (
              <div className="se-section-insert-marker" />
            )}
            {canEdit && (
              <div className="se-add-actions">
                <button
                  type="button"
                  className="se-add-section"
                  aria-label="Agregar etapa o lugar"
                  title="Agregar etapa o lugar"
                  onClick={e => {
                    e.stopPropagation();
                    createSection(phase);
                  }}
                >+ etapa / lugar</button>
                {!singlePhase && phase === 'armado' && onCreateAuxOperation && (
                  <button
                    type="button"
                    className="se-add-section se-add-operation-link"
                    aria-label="Agregar armado previo"
                    title="Crear armado auxiliar para otro día"
                    onClick={e => {
                      e.stopPropagation();
                      onCreateAuxOperation('armado', e.currentTarget);
                    }}
                  >+ armado previo</button>
                )}
                {!singlePhase && phase === 'desarmado' && onCreateAuxOperation && (
                  <button
                    type="button"
                    className="se-add-section se-add-operation-link"
                    aria-label="Agregar desarmado"
                    title="Crear desarmado auxiliar para otro día"
                    onClick={e => {
                      e.stopPropagation();
                      onCreateAuxOperation('desarmado', e.currentTarget);
                    }}
                  >+ DESARMADO</button>
                )}
              </div>
            )}
            </>
            )}
          </div>
        );
        return singlePhase ? line : (
          <div key={phase} className={`se-phase-cell se-phase-cell--${phase}`}>
            {line}
          </div>
        );
      })}

      {menuPos && searchPhase && createPortal(
        <div
          className="se-menu"
          style={{ position: 'fixed', left: menuPos.left, top: menuPos.top, width: Math.max(menuPos.width, 220), zIndex: 2400 }}
          onMouseDown={e => e.preventDefault()}
        >
            {isRoleSearch ? (
              <>
                <div className="se-category-header">Roles</div>
                {roleMenuOptions.map((role, i) => (
                  <button
                    key={role}
                    type="button"
                    className={`se-option${i === activeRoleIndex ? ' se-option--active' : ''}`}
                    onPointerDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      finishPendingRole(role);
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.detail === 0) finishPendingRole(role);
                    }}
                  >
                    {role}
                  </button>
                ))}
                <button
                  type="button"
                  className={`se-option se-option--empty${roleMenuOptions.length === 0 ? ' se-option--active' : ''}`}
                  onPointerDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    finishPendingRole('');
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.detail === 0) finishPendingRole('');
                  }}
                >
                  Sin rol
                </button>
              </>
            ) : menuOptions.length === 0
              ? <button type="button" className="se-option se-option--empty" disabled>Sin resultados</button>
              : menuOptions[0]?.external
                ? menuOptions.map((opt, i) => (
                    <button
                      key={opt.name}
                      type="button"
                      className={`se-option se-option--external${i === activeMenuIndex ? ' se-option--active' : ''}`}
                      onPointerDown={e => {
                        e.preventDefault();
                        selectOption(searchPhase, searchSection, opt, e.currentTarget);
                      }}
                    >
                      + {opt.name}
                    </button>
                  ))
                : (() => {
                    const groups = {};
                    for (const opt of menuOptions) {
                      const cat = opt.category || '';
                      if (!groups[cat]) groups[cat] = [];
                      groups[cat].push(opt);
                    }
                    const cats = [
                      ...categoryOrder.filter(c => groups[c]),
                      ...Object.keys(groups).filter(c => c && !categoryOrder.includes(c)),
                      ...(groups[''] ? [''] : []),
                    ];
                    return cats.map(cat => (
                      <React.Fragment key={cat || '__uncategorized'}>
                        {cat && <div className="se-category-header">{cat}</div>}
                        {groups[cat].map(opt => {
                          const globalIdx = menuOptions.findIndex(o => o.name === opt.name && o.person_id === opt.person_id);
                          return (
                            <button
                              key={opt.name}
                              type="button"
                              className={`se-option${opt.booked ? ' se-option--conflict' : ''}${globalIdx === activeMenuIndex ? ' se-option--active' : ''}`}
                              onPointerDown={e => {
                                e.preventDefault();
                                selectOption(searchPhase, searchSection, opt, e.currentTarget);
                              }}
                            >
                              {opt.name}
                            </button>
                          );
                        })}
                      </React.Fragment>
                    ));
                  })()
            }
        </div>,
        document.body
      )}

      {touchPicker && touchSheet && createPortal(
        <div
          className="se-touch-backdrop"
          role="presentation"
          onClick={() => setTouchSheet(null)}
        >
          <section
            className="se-touch-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={touchSheet.mode === 'people' ? 'Asignar personas' : touchSheet.mode === 'roles' ? 'Elegir rol' : 'Acciones de asignacion'}
            onClick={e => e.stopPropagation()}
          >
            <header className="se-touch-sheet-header">
              <div>
                <p>{phaseLabels[touchSheet.phase] || touchSheet.phase}</p>
                <h3>
                  {touchSheet.mode === 'people' && (touchSheet.replaceTarget ? 'Cambiar persona' : 'Asignar personas')}
                  {touchSheet.mode === 'roles' && `Rol de ${touchSheet.name}`}
                  {touchSheet.mode === 'actions' && touchSheet.name}
                </h3>
              </div>
              <button type="button" onClick={() => setTouchSheet(null)} aria-label="Cerrar">×</button>
            </header>

            {touchSheet.mode === 'people' && (
              <div className="se-touch-options">
                {touchPeopleOptions().length ? touchPeopleOptions().map(opt => (
                  <button
                    key={`${opt.person_id || 'free'}-${opt.name}`}
                    type="button"
                    className={`se-touch-option se-touch-option--person se-touch-option--${opt.availabilityKind || (opt.booked ? 'warning' : 'available')}${opt.disabled ? ' se-touch-option--disabled' : ''}`}
                    disabled={opt.disabled}
                    onClick={() => selectTouchPerson(opt)}
                  >
                    <span className="se-touch-option-main">
                      <b>{opt.name}</b>
                      {opt.category && <small>{opt.category}</small>}
                    </span>
                    <em className="se-touch-option-status">
                      {opt.booked ? (opt.bookedLabel || 'Asignado a otra asignación') : 'Libre'}
                    </em>
                  </button>
                )) : (
                  <p className="se-touch-empty">No quedan personas disponibles para este bloque.</p>
                )}
              </div>
            )}

            {touchSheet.mode === 'roles' && (
              <div className="se-touch-options se-touch-options--roles">
                {touchRoleOptions().map(role => (
                  <button
                    key={role || '__empty'}
                    type="button"
                    className={`se-touch-option${norm(role) === norm(touchSheet.currentRole) ? ' se-touch-option--active' : ''}`}
                    onClick={() => finishTouchRole(role)}
                  >
                    <span>{role || 'Sin rol'}</span>
                  </button>
                ))}
              </div>
            )}

            {touchSheet.mode === 'actions' && (
              <div className="se-touch-actions">
                <div className="se-touch-action-summary">
                  <b>{touchSheet.name}</b>
                  <span>{touchSheet.currentRole || 'Sin rol'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => openTouchRolePicker({ ...touchSheet, source: 'edit' })}
                >
                  Cambiar rol
                </button>
                <button
                  type="button"
                  onClick={() => setTouchSheet({
                    mode: 'people',
                    phase: touchSheet.phase,
                    section: touchSheet.section,
                    replaceTarget: {
                      name: touchSheet.name,
                      assignmentId: touchSheet.assignmentId,
                      personId: touchSheet.currentPersonId || null,
                    },
                  })}
                >
                  Cambiar persona
                </button>
                <button type="button" className="se-touch-danger" onClick={removeTouchAssignment}>
                  Quitar persona
                </button>
                <button type="button" onClick={() => setTouchSheet(null)}>
                  Cancelar
                </button>
              </div>
            )}
          </section>
        </div>,
        document.body
      )}

    </div>
  );
}
